import cn from 'classnames'
import { EMarket } from 'legacy/lut/configs/geo'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo } from 'react'

import { FormMultiselect, updateFormDataField, useGetFormDataField } from 'cng-common/features/form'
import { BaseTemplate } from 'core-components/library/multiSelect/itemsTemplate/BaseTemplate'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'

import { FormFieldValidationLabel } from '../formFieldValidationLabel/FormFieldValidationLabel'

import {
  getBusinessPriorityBadge,
  getBusinessPriorityMarketCode,
  normalizeBusinessPriorityFormValues,
} from './helpers'

import styles from './BusinessPriorities.module.scss'

interface IBusinessPrioritiesProps {
  formKey: string
  initialValue?: string[]
  priorities: IKIBusinessPriority[]
  availablePriorities: IKIBusinessPriority[]
  market?: EMarket | string | null
  fYear: number
  isDisabled?: boolean
  isRequired?: boolean
}

const FIELD_NAME = 'businessPriorities'
const EMPTY_BUSINESS_PRIORITIES: string[] = []

const areBusinessPriorityValuesEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index])

const normalizeBusinessPriorityUid = (value: string): string => value.trim().toLowerCase()

const getBusinessPriorityTemplateUid = (item: Partial<IKIBusinessPriority> | string): string => {
  if (typeof item === 'string') {
    return item
  }

  return item.uid || item.name || ''
}

const getBusinessPriorityTemplateName = (
  item: Partial<IKIBusinessPriority> | string
): string | undefined => {
  if (typeof item === 'string') {
    return undefined
  }

  return item.name
}

export const BusinessPriorities = ({
  formKey,
  initialValue = EMPTY_BUSINESS_PRIORITIES,
  priorities,
  availablePriorities,
  market,
  fYear,
  isDisabled = false,
  isRequired = false,
}: IBusinessPrioritiesProps) => {
  const dispatch = useAppDispatch()
  const normalizedInitialValue = useMemo(
    () => normalizeBusinessPriorityFormValues(initialValue, priorities),
    [initialValue, priorities]
  )
  const [selectedBusinessPriorities = normalizedInitialValue, handleChangeBusinessPriorities] =
    useGetFormDataField<string[]>(formKey, FIELD_NAME)

  const normalizedSelectedBusinessPriorities = useMemo(
    () => normalizeBusinessPriorityFormValues(selectedBusinessPriorities, priorities),
    [priorities, selectedBusinessPriorities]
  )

  const marketCode = useMemo(() => getBusinessPriorityMarketCode(market), [market])

  const businessPriorityOptions = useMemo(() => {
    const availablePriorityUids = new Set(
      availablePriorities.map((priority) => normalizeBusinessPriorityUid(priority.uid))
    )
    const selectedPriorityFallbacks = normalizedSelectedBusinessPriorities
      .filter(
        (priorityUid) =>
          priorityUid && !availablePriorityUids.has(normalizeBusinessPriorityUid(priorityUid))
      )
      .map<IKIBusinessPriority>((priorityUid) => {
        const selectedPriority = priorities.find(
          (priority) =>
            normalizeBusinessPriorityUid(priority.uid) === normalizeBusinessPriorityUid(priorityUid)
        )

        return (
          selectedPriority ?? {
            uid: priorityUid,
            fYear,
            name: priorityUid,
            markets: [],
            offerings: [],
          }
        )
      })

    return [...availablePriorities, ...selectedPriorityFallbacks]
  }, [availablePriorities, fYear, normalizedSelectedBusinessPriorities, priorities])

  const businessPrioritiesCrud = useMemo(
    () =>
      new Crud({
        fetcher: new Static(businessPriorityOptions),
        keyField: 'uid',
        defaultPaging: {
          page: 0,
          pageSize: businessPriorityOptions.length,
        },
      }),
    [businessPriorityOptions]
  )

  const businessPriorityNameByUid = useMemo(() => {
    const nameByUid = new Map<string, string>()

    businessPriorityOptions.concat(priorities).forEach((priority) => {
      nameByUid.set(priority.uid, priority.name)
      nameByUid.set(normalizeBusinessPriorityUid(priority.uid), priority.name)
    })

    return nameByUid
  }, [businessPriorityOptions, priorities])

  useEffect(() => {
    if (
      !areBusinessPriorityValuesEqual(
        selectedBusinessPriorities,
        normalizedSelectedBusinessPriorities
      )
    ) {
      dispatch(
        updateFormDataField({
          formKey,
          field: FIELD_NAME,
          value: normalizedSelectedBusinessPriorities,
          ignoreModified: true,
        })
      )
    }
  }, [dispatch, formKey, normalizedSelectedBusinessPriorities, selectedBusinessPriorities])

  const handlePriorityClick = useCallback(
    (priority: IKIBusinessPriority) => {
      const priorityUid = priority.uid
      const nextBusinessPriorities = normalizedSelectedBusinessPriorities.includes(priorityUid)
        ? normalizedSelectedBusinessPriorities.filter(
            (selectedPriorityUid) => selectedPriorityUid !== priorityUid
          )
        : [...normalizedSelectedBusinessPriorities, priorityUid]

      handleChangeBusinessPriorities(nextBusinessPriorities, true)

      return false
    },
    [handleChangeBusinessPriorities, normalizedSelectedBusinessPriorities]
  )

  return (
    <div className={styles.root}>
      <div className={styles.label}>
        <FormFieldValidationLabel
          text="Business Priorities"
          formKey={formKey}
          fields={[FIELD_NAME]}
        />
      </div>

      <FormMultiselect<IKIBusinessPriority>
        isStatic
        closeOnSelect={false}
        useRowCheckbox
        isDisabled={isDisabled}
        isRequired={isRequired}
        formKey={formKey}
        field={FIELD_NAME}
        defaultValue={normalizedInitialValue}
        source={businessPrioritiesCrud}
        caption="uid"
        placeholder="+ Add priority..."
        classes={{
          controlClasses: {
            root: styles.control,
            popPaper: styles.options,
          },
        }}
        itemClickCallback={handlePriorityClick}
        itemTemplate={(itemTemplateProps) => {
          const businessPriorityUid = getBusinessPriorityTemplateUid(itemTemplateProps.item)
          const businessPriorityName =
            businessPriorityNameByUid.get(businessPriorityUid) ||
            businessPriorityNameByUid.get(normalizeBusinessPriorityUid(businessPriorityUid)) ||
            getBusinessPriorityTemplateName(itemTemplateProps.item) ||
            businessPriorityUid
          const businessPriorityItem: IKIBusinessPriority = {
            ...(typeof itemTemplateProps.item === 'string' ? {} : itemTemplateProps.item),
            uid: businessPriorityUid,
            name: businessPriorityName,
            fYear:
              typeof itemTemplateProps.item === 'string' ? fYear : itemTemplateProps.item.fYear,
            markets:
              typeof itemTemplateProps.item === 'string'
                ? []
                : (itemTemplateProps.item.markets ?? []),
            offerings:
              typeof itemTemplateProps.item === 'string'
                ? []
                : (itemTemplateProps.item.offerings ?? []),
          }

          return (
            <BaseTemplate
              {...itemTemplateProps}
              item={businessPriorityItem}
              caption="name"
              tooltipValue={businessPriorityName}
            />
          )
        }}
        renderFunction={(priority) => {
          const badge = getBusinessPriorityBadge(priority, marketCode ?? 'Market')

          return (
            <div className={styles.option}>
              <span>{priority.name}</span>
              <span
                className={cn(styles.badge, {
                  [styles.badgeWorldwide]: badge.isWorldwide,
                  [styles.badgeMarket]: !badge.isWorldwide,
                })}
              >
                {badge.label}
              </span>
            </div>
          )
        }}
      />
    </div>
  )
}

BusinessPriorities.displayName = 'BusinessPriorities'
